import * as THREE from 'three';
import { disposeAllMeshes } from '../Util.js';

const MARKER_COLOR_A = 0x00ff88;
const MARKER_COLOR_B = 0xff8800;
const LINE_COLOR = 0xffff00;
const MARKER_RADIUS = 0.08;

/**
 * Ruler: Two-click measurement tool. Click two points on the splat mesh to measure the distance between them.
 */
export class Ruler {

    constructor(container) {
        this.container = container || document.body;
        this.active = false;
        this.pointA = null;
        this.pointB = null;
        this.waitingForSecondPoint = false;

        this.root = new THREE.Object3D();
        this.markerA = null;
        this.markerB = null;
        this.line = null;

        this.midpoint = new THREE.Vector3();
        this.labelPosition = new THREE.Vector3();

        this.setupDOM();
    }

    setupDOM() {
        this.domContainer = document.createElement('div');
        this.domContainer.style.pointerEvents = 'none';
        this.domContainer.style.position = 'absolute';
        this.domContainer.style.top = '0';
        this.domContainer.style.left = '0';
        this.domContainer.style.width = '100%';
        this.domContainer.style.height = '100%';
        this.domContainer.style.zIndex = '9998';

        const style = document.createElement('style');
        style.innerHTML = `
            .ruler-hint {
                position: absolute;
                bottom: 20px;
                left: 50%;
                transform: translateX(-50%);
                padding: 8px 16px;
                background-color: rgba(30, 30, 30, 0.85);
                border: 1px solid #666;
                border-radius: 6px;
                color: #eeeeee;
                font-family: arial, sans-serif;
                font-size: 12pt;
                white-space: nowrap;
            }

            .ruler-label {
                position: absolute;
                padding: 4px 10px;
                background-color: rgba(20, 20, 20, 0.9);
                border: 2px solid #ffff00;
                border-radius: 4px;
                color: #ffff00;
                font-family: arial, sans-serif;
                font-size: 13pt;
                font-weight: bold;
                white-space: nowrap;
                transform: translate(-50%, -100%);
                margin-top: -8px;
            }
        `;
        this.domContainer.appendChild(style);

        this.hintElement = document.createElement('div');
        this.hintElement.className = 'ruler-hint';
        this.hintElement.style.display = 'none';
        this.domContainer.appendChild(this.hintElement);

        this.labelElement = document.createElement('div');
        this.labelElement.className = 'ruler-label';
        this.labelElement.style.display = 'none';
        this.domContainer.appendChild(this.labelElement);

        this.container.appendChild(this.domContainer);
    }

    setContainer(container) {
        if (this.container && this.domContainer.parentElement === this.container) {
            this.container.removeChild(this.domContainer);
        }
        if (container) {
            this.container = container;
            this.container.appendChild(this.domContainer);
        }
    }

    setActive(active) {
        this.active = active;
        if (!active) {
            this.clear();
            this.hintElement.style.display = 'none';
        } else {
            this.updateHint();
            this.hintElement.style.display = 'block';
        }
    }

    isActive() {
        return this.active;
    }

    clear() {
        this.pointA = null;
        this.pointB = null;
        this.waitingForSecondPoint = false;
        this.clearVisuals();
        this.labelElement.style.display = 'none';
        if (this.active) this.updateHint();
    }

    clearVisuals() {
        if (this.markerA) {
            disposeAllMeshes(this.markerA);
            this.root.remove(this.markerA);
            this.markerA = null;
        }
        if (this.markerB) {
            disposeAllMeshes(this.markerB);
            this.root.remove(this.markerB);
            this.markerB = null;
        }
        if (this.line) {
            this.line.geometry.dispose();
            this.line.material.dispose();
            this.root.remove(this.line);
            this.line = null;
        }
    }

    createMarker(color) {
        const geometry = new THREE.SphereGeometry(MARKER_RADIUS, 16, 16);
        const material = new THREE.MeshBasicMaterial({
            color: color,
            depthTest: true,
            depthWrite: true
        });
        const marker = new THREE.Mesh(geometry, material);
        marker.renderOrder = 10;
        return marker;
    }

    updateLine() {
        if (this.line) {
            this.line.geometry.dispose();
            this.root.remove(this.line);
            this.line = null;
        }

        if (this.pointA && this.pointB) {
            const geometry = new THREE.BufferGeometry().setFromPoints([this.pointA, this.pointB]);
            const material = new THREE.LineBasicMaterial({
                color: LINE_COLOR,
                depthTest: true,
                depthWrite: true,
                linewidth: 2
            });
            this.line = new THREE.Line(geometry, material);
            this.line.renderOrder = 9;
            this.root.add(this.line);
        }
    }

    updateMeasurementLabel() {
        if (!this.pointA || !this.pointB) {
            this.labelElement.style.display = 'none';
            return;
        }

        const distance = this.pointA.distanceTo(this.pointB);
        this.labelElement.innerHTML = `${distance.toFixed(3)} units`;
        this.labelElement.style.display = 'block';
        this.midpoint.copy(this.pointA).add(this.pointB).multiplyScalar(0.5);
    }

    updateHint() {
        if (!this.active) return;
        if (!this.pointA) {
            this.hintElement.innerHTML = 'Ruler: Click first point';
        } else if (this.waitingForSecondPoint) {
            this.hintElement.innerHTML = 'Ruler: Click second point';
        } else {
            this.hintElement.innerHTML = 'Ruler: Click to start new measurement (R to exit)';
        }
    }

    /**
     * Place a point at the given 3D world position.
     */
    placePoint(position) {
        if (!this.pointA || (this.pointB && !this.waitingForSecondPoint)) {
            this.clearVisuals();
            this.pointA = position.clone();
            this.pointB = null;
            this.waitingForSecondPoint = true;

            this.markerA = this.createMarker(MARKER_COLOR_A);
            this.markerA.position.copy(this.pointA);
            this.root.add(this.markerA);
        } else if (this.waitingForSecondPoint) {
            this.pointB = position.clone();
            this.waitingForSecondPoint = false;

            this.markerB = this.createMarker(MARKER_COLOR_B);
            this.markerB.position.copy(this.pointB);
            this.root.add(this.markerB);

            this.updateLine();
            this.updateMeasurementLabel();
        }

        this.updateHint();
        return true;
    }

    /**
     * Update the on-screen label position based on the current camera.
     */
    updateLabelPosition(camera, renderDimensions) {
        if (!this.pointA || !this.pointB || this.labelElement.style.display === 'none') return;

        this.midpoint.copy(this.pointA).add(this.pointB).multiplyScalar(0.5);
        this.labelPosition.copy(this.midpoint).project(camera);

        const x = (this.labelPosition.x * 0.5 + 0.5) * renderDimensions.x;
        const y = (-this.labelPosition.y * 0.5 + 0.5) * renderDimensions.y;

        if (this.labelPosition.z > 1) {
            this.labelElement.style.display = 'none';
        } else {
            this.labelElement.style.display = 'block';
            this.labelElement.style.left = `${x}px`;
            this.labelElement.style.top = `${y}px`;
        }
    }

    getRoot() {
        return this.root;
    }

    hasVisuals() {
        return this.markerA !== null || this.markerB !== null || this.line !== null;
    }

    dispose() {
        this.clearVisuals();
        if (this.domContainer.parentElement) {
            this.domContainer.parentElement.removeChild(this.domContainer);
        }
    }

}
